import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Form, Modal, Radio, Space, Typography } from 'antd';
import React, { useEffect } from 'react';

import { getTotalPayment } from './helpers';
import styles from './index.module.scss';
import type { PaymentInfo } from './types';

const { confirm } = Modal;
const { Item, useForm } = Form;
const { Text } = Typography;

type PaymentFormValues = {
  payMethod: 'credit' | 'point';
};

type PaymentModalProps = {
  open: boolean;
  closeModal: () => void;
  paymentInfo: PaymentInfo;
  currentMemberPoint: number;
};

export const PaymentModal = ({
  open,
  closeModal,
  paymentInfo,
  currentMemberPoint,
}: PaymentModalProps) => {
  const [form] = useForm<PaymentFormValues>();

  const { paymentPrice, paymentPoints } = getTotalPayment(paymentInfo);

  const createOrder = async () => {
    try {
      await form.validateFields();

      confirm({
        title: '確定送出育樂預約？',
        content: '確認資料無誤!!',
        icon: <ExclamationCircleOutlined rev={undefined} />,
        centered: true,
        onOk: () => {
          // TODO: 這邊要打API去建立訂單，邏輯待寫
          setTimeout(() => {
            closeModal();
          }, 1500);
        },
      });
    } catch {
      return;
    }
  };

  const finishPayment = async () => {
    try {
      await form.validateFields();

      confirm({
        title: '確定立即支付完成育樂預約？',
        content: '確認資料無誤!!',
        icon: <ExclamationCircleOutlined rev={undefined} />,
        centered: true,
        onOk: () => {
          // TODO: 這邊要打API去完成付款，邏輯待寫
          setTimeout(() => {
            closeModal();
          }, 1500);
        },
      });
    } catch {
      return;
    }
  };

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [form, open]);

  const remainingPoints = currentMemberPoint - paymentPoints;

  return (
    <Modal
      title="支付資訊"
      open={open}
      onCancel={closeModal}
      centered
      destroyOnClose
      maskClosable={false}
      footer={[
        <Button key="order" type="primary" onClick={createOrder}>
          送出預約
        </Button>,
        <Button key="pay" onClick={finishPayment}>
          立即付款
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Space direction="vertical" className={styles['w-full']}>
          <Space direction="vertical" className={styles['w-full']}>
            <Text type="secondary">消費金額</Text>
            <div className={styles.totalPayment}>
              {paymentPrice ? (
                <Item label="台幣">
                  <Text>{paymentPrice} 元</Text>
                </Item>
              ) : null}

              {paymentPoints ? (
                <Item label="點數">
                  <Text>{paymentPoints} 點</Text>
                </Item>
              ) : null}
            </div>
          </Space>

          <Space direction="vertical" className={styles['w-full']}>
            <Text type="secondary" className="required">
              付費方式
            </Text>

            <Item noStyle shouldUpdate>
              {({ getFieldError }) => {
                const errors = getFieldError('payMethod');
                return errors.length > 0 ? (
                  <div className="ant-form-item-explain-error">{errors[0]}</div>
                ) : null;
              }}
            </Item>

            <Item
              name="payMethod"
              rules={[{ required: true, message: '請選擇付費方式' }]}
              noStyle
            >
              <Radio.Group className={styles['w-full']}>
                <Space
                  direction="vertical"
                  size="middle"
                  className={styles['w-full']}
                >
                  <Radio value="credit">掛帳</Radio>

                  <Radio value="point" disabled={remainingPoints < 0}>
                    點數
                    {remainingPoints < 0 && (
                      <p className={styles.warningMessage}>
                        (餘額不足，無法使用點數支付)
                      </p>
                    )}
                  </Radio>
                  <div className={styles.pointInfo}>
                    <div className={styles.item}>
                      <h5>現有點數：</h5>
                      <p>{currentMemberPoint}點</p>
                    </div>
                    <div className={styles.item}>
                      <h5>扣除點數：</h5>
                      <p>{paymentPoints}點</p>
                    </div>
                    <div className={styles.item}>
                      <h5>預計剩餘點數：</h5>
                      <p>{remainingPoints}點</p>
                    </div>
                  </div>
                </Space>
              </Radio.Group>
            </Item>
          </Space>
        </Space>
      </Form>

      <div className={styles.tipBox}>
        <p>
          若選擇送出預約，會員需在訂單成立後 5
          分鐘內完成付款，若逾時訂單將自動取消，進入歷史資料。
        </p>
      </div>
    </Modal>
  );
};
